// Cliente HTTP do web: fetch same-origin com cookie, erro tipado e redirecionamento de sessão.
// Base da API e credencial vêm da plataforma (`@plataforma/api`): web = base "" e cookie same-origin; o app
// nativo troca só isso (ADR app nativo, D1).
//
//   const inicio = await api.get<Inicio>("/api/eu/inicio");
//   await api.post<MensagemItem>("/api/eu/conversa/mensagens", { texto, chaveIdempotencia });
//   await api.enviarBinario<MensagemItem>("/api/eu/conversa/audio", blob, { duracaoSeg, chaveIdempotencia });
//
// Tipos de request/response vêm SÓ como tipo de `@grindset/server/contratos` (nenhum código do servidor no bundle).
// 401 NAO_AUTENTICADO -> login da área certa (aluno "/entrar", equipe "/painel/entrar") com `volta` e `expirou=1`.
// 403 TROCA_DE_SENHA_OBRIGATORIA -> "/painel/trocar-senha".
import type { CodigoErro, RespostaErro } from "@grindset/server/contratos";
import { aoResponder, baseApi, cabecalhosCredencial, credenciaisApi, marcaDaSessao } from "@plataforma/api";

export type CodigoErroCliente = CodigoErro | "SEM_CONEXAO" | "RESPOSTA_INVALIDA";

export class ErroApi extends Error {
  readonly status: number;
  readonly codigo: CodigoErroCliente;
  readonly detalhes: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(
    status: number,
    codigo: CodigoErroCliente,
    mensagem: string,
    detalhes?: Record<string, unknown>,
    requestId?: string,
  ) {
    super(mensagem);
    this.name = "ErroApi";
    this.status = status;
    this.codigo = codigo;
    this.detalhes = detalhes;
    this.requestId = requestId;
  }

  /** Mensagem de campo vinda de `detalhes` (erros VALIDACAO). */
  campo(nome: string): string | undefined {
    const v = this.detalhes?.[nome];
    return typeof v === "string" ? v : undefined;
  }
}

export const ehErroApi = (e: unknown, codigo?: CodigoErroCliente): e is ErroApi =>
  e instanceof ErroApi && (codigo === undefined || e.codigo === codigo);

/**
 * 409 ESTADO_INVALIDO `{ termosPendentes: true }`: escrita do aluno recusada até o novo aceite (TERMOS_TRAVA_ESCRITA
 * no servidor). O cliente recarrega a sessão e a folha "Termos atualizados" abre; a tela não mostra erro próprio.
 */
export const ehTermosPendentes = (e: unknown): e is ErroApi =>
  ehErroApi(e, "ESTADO_INVALIDO") && e.detalhes?.termosPendentes === true;

/** 409 ESTADO_INVALIDO `{ onboarding: true }`: o aluno nunca aceitou os termos; o cliente leva ao onboarding. */
export const ehOnboardingPendente = (e: unknown): e is ErroApi =>
  ehErroApi(e, "ESTADO_INVALIDO") && e.detalhes?.onboarding === true;

/** Texto de 429 (LIMITE_EXCEDIDO) nas telas de erro de carga (QA D-07). */
export const MENSAGEM_LIMITE = "Muitas tentativas em pouco tempo. Espere um minuto e tente de novo.";

// ── Reações globais (configuradas no main.tsx com o router) ─────────────────

export interface ManipuladoresCliente {
  aoPerderSessao(destino: string): void;
  aoExigirTrocaDeSenha(): void;
  /** Termos pendentes de novo aceite: recarregar a sessão (a folha "Termos atualizados" abre). */
  aoExigirNovoAceite(): void;
  /** Onboarding pendente (nunca aceitou os termos): levar a /boas-vindas/1. */
  aoExigirOnboarding(): void;
}

let manipuladores: ManipuladoresCliente = {
  aoPerderSessao: (destino) => window.location.assign(destino),
  aoExigirTrocaDeSenha: () => window.location.assign("/painel/trocar-senha"),
  aoExigirNovoAceite: () => undefined,
  aoExigirOnboarding: () => window.location.assign("/boas-vindas/1"),
};

export function configurarCliente(m: Partial<ManipuladoresCliente>): void {
  manipuladores = { ...manipuladores, ...m };
}

/** Login da área atual com retorno para onde o usuário estava. */
export function destinoDeLogin(
  caminhoAtual: string = window.location.pathname + window.location.search,
): string {
  const login = caminhoAtual.startsWith("/painel") ? "/painel/entrar" : "/entrar";
  const volta = caminhoAtual.startsWith(login) ? "" : `&volta=${encodeURIComponent(caminhoAtual)}`;
  return `${login}?expirou=1${volta}`;
}

// ── Requisição ──────────────────────────────────────────────────────────────

export type ValorConsulta = string | number | boolean | null | undefined;
export type Consulta = Record<string, ValorConsulta>;

export interface OpcoesRequisicao {
  consulta?: Consulta;
  sinal?: AbortSignal;
  /** Não redireciona em 401 (ex.: GET /api/auth/sessao, telas de login). */
  semRedirecionar?: boolean;
  cabecalhos?: Record<string, string>;
  /**
   * Prazo (ms) para a resposta chegar. Estourou: aborta e lança `SEM_CONEXAO`, como a rede caída (rede que "pendura"
   * sem cair não deixa a tela presa em "Enviando…"). Sem prazo, espera o quanto o navegador esperar.
   */
  prazoMs?: number;
}

type Metodo = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** URL da requisição: base da API da plataforma (web: "", caminho relativo) + caminho + query. */
export function montarUrl(caminho: string, consulta?: Consulta, base: string = baseApi): string {
  const url = base + caminho;
  if (!consulta) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(consulta)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function lerErro(resposta: Response): Promise<ErroApi> {
  let corpo: Partial<RespostaErro> | null = null;
  try {
    corpo = (await resposta.json()) as Partial<RespostaErro>;
  } catch {
    corpo = null;
  }
  const e = corpo?.erro;
  if (e?.codigo) return new ErroApi(resposta.status, e.codigo, e.mensagem ?? "Erro", e.detalhes, e.requestId);
  // 429 sem o corpo padrão (ex.: vindo de um proxy): ainda é limite, não erro do servidor.
  if (resposta.status === 429) return new ErroApi(429, "LIMITE_EXCEDIDO", MENSAGEM_LIMITE);
  return new ErroApi(
    resposta.status,
    "INTERNO",
    "Algo deu errado do nosso lado. Tente de novo em instantes.",
  );
}

async function executar(
  metodo: Metodo,
  caminho: string,
  corpo: BodyInit | undefined,
  tipo: string | undefined,
  op: OpcoesRequisicao,
) {
  let resposta: Response;
  // Prazo: um controlador próprio, encadeado ao sinal de quem chamou (o cancelamento dele continua valendo).
  const prazo = op.prazoMs ? new AbortController() : null;
  let estourou = false;
  const relogio = prazo
    ? setTimeout(() => {
        estourou = true;
        prazo.abort();
      }, op.prazoMs)
    : undefined;
  const repassar = () => prazo?.abort();
  // App: troca de sessão em que a requisição sai (a resposta de uma sessão que já acabou não mexe na nova).
  const marca = __ALVO__ === "nativo" ? marcaDaSessao() : 0;
  if (prazo && op.sinal) {
    if (op.sinal.aborted) prazo.abort();
    else op.sinal.addEventListener("abort", repassar, { once: true });
  }
  try {
    resposta = await fetch(montarUrl(caminho, op.consulta), {
      method: metodo,
      credentials: credenciaisApi,
      headers: {
        accept: "application/json",
        ...(tipo ? { "content-type": tipo } : {}),
        ...cabecalhosCredencial(),
        ...op.cabecalhos,
      },
      body: corpo,
      signal: prazo?.signal ?? op.sinal,
    });
  } catch (e) {
    // App nativo: a sessão do aparelho reage à resposta (token novo, 401, sair). No web este ramo nem existe.
    if (__ALVO__ === "nativo") await aoResponder(caminho, null, marca);
    if ((e as Error).name === "AbortError" && !estourou) throw e;
    throw new ErroApi(0, "SEM_CONEXAO", "Sem conexão. Confira sua internet e tente de novo.");
  } finally {
    clearTimeout(relogio);
    op.sinal?.removeEventListener("abort", repassar);
  }
  if (__ALVO__ === "nativo") await aoResponder(caminho, resposta, marca);

  if (!resposta.ok) {
    const erro = await lerErro(resposta);
    if (!op.semRedirecionar) {
      if (erro.codigo === "NAO_AUTENTICADO") manipuladores.aoPerderSessao(destinoDeLogin());
      if (erro.codigo === "TROCA_DE_SENHA_OBRIGATORIA") manipuladores.aoExigirTrocaDeSenha();
      if (ehTermosPendentes(erro)) manipuladores.aoExigirNovoAceite();
      else if (ehOnboardingPendente(erro)) manipuladores.aoExigirOnboarding();
    }
    throw erro;
  }
  return resposta;
}

async function comoJson<T>(resposta: Response): Promise<T> {
  if (resposta.status === 204 || resposta.headers.get("content-length") === "0") return undefined as T;
  try {
    return (await resposta.json()) as T;
  } catch {
    throw new ErroApi(resposta.status, "RESPOSTA_INVALIDA", "Resposta inesperada do servidor.");
  }
}

function json<T>(metodo: Metodo, caminho: string, corpo: unknown, op: OpcoesRequisicao = {}): Promise<T> {
  const temCorpo = corpo !== undefined;
  return executar(
    metodo,
    caminho,
    temCorpo ? JSON.stringify(corpo) : undefined,
    temCorpo ? "application/json" : undefined,
    op,
  ).then(comoJson<T>);
}

export const api = {
  get: <T>(caminho: string, op?: OpcoesRequisicao) => json<T>("GET", caminho, undefined, op),
  post: <T = void>(caminho: string, corpo?: unknown, op?: OpcoesRequisicao) =>
    json<T>("POST", caminho, corpo, op),
  put: <T = void>(caminho: string, corpo?: unknown, op?: OpcoesRequisicao) =>
    json<T>("PUT", caminho, corpo, op),
  patch: <T = void>(caminho: string, corpo?: unknown, op?: OpcoesRequisicao) =>
    json<T>("PATCH", caminho, corpo, op),
  /** DELETE; `corpo` quando a rota pede (ex.: desfazer entrega de conquista com motivo). */
  delete: <T = void>(caminho: string, op?: OpcoesRequisicao, corpo?: unknown) => json<T>("DELETE", caminho, corpo, op),

  /** Corpo binário cru (áudio, foto) — metadados na query (SPEC §13.14). */
  enviarBinario: <T>(caminho: string, dados: Blob, consulta?: Consulta, op: OpcoesRequisicao = {}) =>
    executar("POST", caminho, dados, dados.type || "application/octet-stream", { ...op, consulta }).then(
      comoJson<T>,
    ),

  /** PUT binário (PUT /api/eu/foto). */
  substituirBinario: <T>(caminho: string, dados: Blob, op: OpcoesRequisicao = {}) =>
    executar("PUT", caminho, dados, dados.type || "application/octet-stream", op).then(comoJson<T>),

  /** Texto cru (importação CSV: text/csv). */
  enviarTexto: <T>(
    caminho: string,
    texto: string,
    tipo = "text/csv",
    consulta?: Consulta,
    op: OpcoesRequisicao = {},
  ) => executar("POST", caminho, texto, `${tipo}; charset=utf-8`, { ...op, consulta }).then(comoJson<T>),

  /** Download (exportação CSV). Devolve o Blob e o nome sugerido pelo servidor. */
  async baixar(
    caminho: string,
    consulta?: Consulta,
    op: OpcoesRequisicao = {},
  ): Promise<{ blob: Blob; nome: string | null }> {
    const r = await executar("GET", caminho, undefined, undefined, { ...op, consulta });
    const disp = r.headers.get("content-disposition") ?? "";
    const nome = /filename="?([^";]+)"?/.exec(disp)?.[1] ?? null;
    return { blob: await r.blob(), nome };
  },
};

/** UUID v4 para `chaveIdempotencia` (rotas marcadas com (I) na SPEC). */
export const novaChaveIdempotencia = (): string => crypto.randomUUID();
